import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  FileText,
  Trash2,
  Save,
  User,
  Building2,
  Mail,
  MapPin,
  KeyRound,
  Copy,
  CheckCircle2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, StatusBadge, formatDateTime } from "../ui";
import { formatCpfCnpj, formatPhone } from "../../lib/masks";

const ONBOARDING_DOC_LABELS = {
  documento_identificacao: "Documento de identificação",
  comprovante_endereco: "Comprovante de endereço",
  contrato_social: "Contrato Social",
  documento_representante: "Documento do representante legal",
};

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  cpf_cnpj: "",
  person_type: "PF",
  status: "ativo",
  tipo: "",
  address: "",
  neighborhood: "",
  city: "",
  cep: "",
  uf: "",
  boleto_email: "",
  financial_contact_name: "",
  financial_contact_email: "",
  financial_contact_phone: "",
  legal_rep_name: "",
  legal_rep_role: "",
  legal_rep_email: "",
  legal_rep_phone: "",
  notes: "",
};

export default function AdminAssociadoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tipos, setTipos] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(
    () =>
      api
        .get(`/admin/associados/${id}`)
        .then((r) => {
          setUser(r.data);
          setForm({
            ...EMPTY_FORM,
            ...Object.fromEntries(
              Object.keys(EMPTY_FORM).map((k) => [k, r.data[k] ?? EMPTY_FORM[k]])
            ),
            tipo: r.data.tipo || "Contribuinte",
            person_type: r.data.person_type || "PJ",
          });
        })
        .catch(() => toast.error("Associado não encontrado")),
    [id]
  );

  useEffect(() => {
    load();
    api
      .get("/portal/meta/tipos")
      .then((r) => setTipos(r.data))
      .catch((e) => console.warn("Falha ao carregar tipos:", e));
  }, [load]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const inputChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Only send fields that changed vs the loaded user to avoid overwriting
      // with same value; but keeping simple: send all editable fields.
      const payload = { ...form };
      await api.patch(`/admin/associados/${id}`, payload);
      toast.success("Perfil atualizado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadDoc = async (docType, entry) => {
    try {
      const res = await api.get(
        `/admin/associados/${id}/onboarding/${docType}/download`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = entry?.file_name || `${docType}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao baixar o documento.");
    }
  };

  const deleteDoc = async (docType) => {
    if (
      !window.confirm(
        `Excluir o documento "${
          ONBOARDING_DOC_LABELS[docType] || docType
        }" deste associado?`
      )
    )
      return;
    try {
      await api.delete(`/admin/associados/${id}/onboarding/${docType}`);
      toast.success("Documento excluído");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resetPassword = async () => {
    if (
      !window.confirm(
        `Redefinir a senha de ${user.name}?\n\n` +
          `Uma senha aleatória será gerada e enviada por e-mail ao contato principal ` +
          `e ao representante legal. A senha também será exibida na tela para você repassar.`
      )
    )
      return;
    setResetBusy(true);
    setCopied(false);
    try {
      const r = await api.post(`/admin/associados/${id}/reset-password`);
      setResetResult(r.data);
      if (r.data.email?.ok) {
        toast.success(
          `Senha redefinida e enviada por e-mail para ${r.data.recipients.length} destinatário(s).`
        );
      } else {
        toast.warning(
          "Senha redefinida — mas o e-mail não foi entregue. Copie a senha abaixo e repasse manualmente."
        );
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResetBusy(false);
    }
  };

  const copyPassword = () => {
    if (!resetResult?.temporary_password) return;
    navigator.clipboard.writeText(resetResult.temporary_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!user) return <div className="text-sm text-slate-500">Carregando...</div>;

  const onboardingEntries = Object.entries(user.onboarding_docs || {}).filter(
    ([, v]) => v && v.file_name
  );

  return (
    <div data-testid="admin-associado-detail" className="pb-10">
      <Link
        to="/admin/associados"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-[#002776]"
      >
        <ArrowLeft size={14} /> Voltar aos associados
      </Link>
      <PageHeader
        title={user.name}
        subtitle={user.email}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={resetPassword}
              disabled={resetBusy}
              data-testid="reset-password-btn"
              className="inline-flex items-center gap-2 border border-slate-300 px-3 py-2 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776] hover:text-[#002776] disabled:opacity-50"
            >
              <KeyRound size={14} /> {resetBusy ? "Gerando..." : "Redefinir senha"}
            </button>
            <StatusBadge status={user.status} />
          </div>
        }
      />

      <form onSubmit={save} className="space-y-5" data-testid="assoc-edit-form">
        {/* Identificação */}
        <SectionCard icon={User} title="Identificação">
          <Grid>
            <Field label="Nome / Razão Social *" value={form.name} onChange={upd("name")} testid="edit-name" />
            <div>
              <Label>Tipo de pessoa *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["PF", "PJ"].map((v) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => upd("person_type")(v)}
                    data-testid={`edit-person-${v}`}
                    className={`px-3 py-2.5 border text-[11px] uppercase tracking-[0.22em] rounded-sm ${
                      form.person_type === v
                        ? "bg-[#0A0F14] text-white border-[#0A0F14]"
                        : "border-slate-300 hover:border-[#002776]"
                    }`}
                  >
                    {v === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}
                  </button>
                ))}
              </div>
            </div>
            <MaskedField
              label={form.person_type === "PJ" ? "CNPJ" : "CPF"}
              value={form.cpf_cnpj}
              onChange={(v) => upd("cpf_cnpj")(formatCpfCnpj(v, form.person_type))}
              maxLength={form.person_type === "PJ" ? 18 : 14}
              placeholder={form.person_type === "PJ" ? "XX.XXX.XXX/XXXX-XX" : "XXX.XXX.XXX-XX"}
              testid="edit-doc"
            />
            <Field
              label="Tipo de Associado"
              value={form.tipo}
              onChange={upd("tipo")}
              testid="edit-tipo"
              as="select"
              options={tipos.map((t) => (typeof t === "string" ? t : t.name))}
            />
            <Field
              label="Status"
              value={form.status}
              onChange={upd("status")}
              testid="edit-status"
              as="select"
              options={["ativo", "pendente", "inativo"]}
            />
          </Grid>
        </SectionCard>

        {/* Contato principal */}
        <SectionCard icon={Mail} title="Contato principal">
          <Grid>
            <Field
              label="E-mail principal *"
              value={form.email}
              onChange={upd("email")}
              testid="edit-email"
              placeholder="contato@empresa.com.br"
              type="email"
            />
            <MaskedField
              label="Telefone"
              value={form.phone}
              onChange={(v) => upd("phone")(formatPhone(v))}
              maxLength={15}
              placeholder="(11) 99999-9999"
              testid="edit-phone"
            />
          </Grid>
        </SectionCard>

        {/* Endereço */}
        <SectionCard icon={MapPin} title="Endereço">
          <Grid>
            <Field
              label="Endereço (logradouro, nº, complemento)"
              value={form.address}
              onChange={upd("address")}
              testid="edit-address"
              wide
            />
            <Field label="Bairro" value={form.neighborhood} onChange={upd("neighborhood")} testid="edit-neighborhood" />
            <Field label="Cidade" value={form.city} onChange={upd("city")} testid="edit-city" />
            <Field
              label="UF"
              value={form.uf}
              onChange={(v) => upd("uf")(v.toUpperCase().slice(0, 2))}
              testid="edit-uf"
              maxLength={2}
              placeholder="SP"
            />
            <Field label="CEP" value={form.cep} onChange={upd("cep")} testid="edit-cep" placeholder="00000-000" />
          </Grid>
        </SectionCard>

        {/* Financeiro */}
        <SectionCard icon={Mail} title="Financeiro (boletos e recibos)">
          <Grid>
            <Field
              label="E-mail para envio de Boletos"
              value={form.boleto_email}
              onChange={upd("boleto_email")}
              testid="edit-boleto-email"
              type="email"
              wide
            />
            <Field
              label="Nome do contato financeiro"
              value={form.financial_contact_name}
              onChange={upd("financial_contact_name")}
              testid="edit-fin-name"
            />
            <MaskedField
              label="Telefone financeiro"
              value={form.financial_contact_phone}
              onChange={(v) => upd("financial_contact_phone")(formatPhone(v))}
              maxLength={15}
              testid="edit-fin-phone"
            />
            <Field
              label="E-mail financeiro"
              value={form.financial_contact_email}
              onChange={upd("financial_contact_email")}
              testid="edit-fin-email"
              type="email"
              wide
            />
          </Grid>
        </SectionCard>

        {/* Representante Legal (PJ) */}
        {form.person_type === "PJ" && (
          <SectionCard icon={Building2} title="Representante Legal (PJ)">
            <Grid>
              <Field
                label="Nome do Representante Legal"
                value={form.legal_rep_name}
                onChange={upd("legal_rep_name")}
                testid="edit-lr-name"
                wide
              />
              <Field
                label="Cargo"
                value={form.legal_rep_role}
                onChange={upd("legal_rep_role")}
                testid="edit-lr-role"
                placeholder="Ex.: Diretor Administrativo"
              />
              <Field
                label="E-mail do Representante"
                value={form.legal_rep_email}
                onChange={upd("legal_rep_email")}
                testid="edit-lr-email"
                type="email"
              />
              <MaskedField
                label="Telefone do Representante"
                value={form.legal_rep_phone}
                onChange={(v) => upd("legal_rep_phone")(formatPhone(v))}
                maxLength={15}
                testid="edit-lr-phone"
              />
            </Grid>
          </SectionCard>
        )}

        {/* Observações */}
        <SectionCard title="Observações internas">
          <textarea
            data-testid="edit-notes"
            value={form.notes || ""}
            onChange={inputChange("notes")}
            rows={3}
            placeholder="Anotações visíveis apenas para o admin"
            className="w-full border border-slate-300 px-3 py-2.5 rounded-sm"
          />
        </SectionCard>

        <div className="flex items-center gap-3 sticky bottom-0 bg-[#F4F4F0]/95 backdrop-blur py-3 border-t border-slate-200">
          <button
            data-testid="edit-save"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Salvando..." : "Salvar alterações"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/associados")}
            className="px-5 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-slate-400"
          >
            Cancelar
          </button>
          <span className="ml-auto text-xs text-slate-500">
            Criado em {formatDateTime(user.created_at)}
          </span>
        </div>
      </form>

      {/* Onboarding docs */}
      <div className="mt-8">
        <h3 className="font-serif-display text-xl mb-3">
          Documentos de cadastro
        </h3>
        {onboardingEntries.length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-sm p-6 text-center">
            Nenhum documento de cadastro enviado por este associado.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {onboardingEntries.map(([docType, entry]) => (
              <div
                key={docType}
                className="border border-slate-200 rounded-sm p-4 flex items-center gap-3"
              >
                <FileText size={18} className="text-[#002776]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {ONBOARDING_DOC_LABELS[docType] || docType}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {entry.file_name}
                  </div>
                </div>
                <button
                  onClick={() => downloadDoc(docType, entry)}
                  className="p-2 border border-slate-300 hover:border-[#002776]"
                  title="Baixar"
                >
                  <Download size={14} />
                </button>
                <button
                  onClick={() => deleteDoc(docType)}
                  className="p-2 border border-rose-300 hover:bg-rose-50 text-rose-700"
                  title="Excluir"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {resetResult && (
        <ResetPasswordModal
          data={resetResult}
          copied={copied}
          onCopy={copyPassword}
          onClose={() => {
            setResetResult(null);
            setCopied(false);
          }}
        />
      )}
    </div>
  );
}

// ---------- shared UI ----------
const SectionCard = ({ icon: Icon, title, children }) => (
  <Card className="p-6">
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon size={16} className="text-[#002776]" />}
      <h3 className="font-serif-display text-xl">{title}</h3>
    </div>
    {children}
  </Card>
);

const Grid = ({ children }) => (
  <div className="grid md:grid-cols-2 gap-4">{children}</div>
);

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
    {children}
  </label>
);

const Field = ({
  label,
  value,
  onChange,
  testid,
  placeholder,
  type = "text",
  maxLength,
  wide = false,
  as,
  options,
}) => (
  <div className={wide ? "md:col-span-2" : ""}>
    <Label>{label}</Label>
    {as === "select" ? (
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
      >
        {(options || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
      />
    )}
  </div>
);

const MaskedField = (props) => <Field {...props} />;

function ResetPasswordModal({ data, copied, onCopy, onClose }) {
  const emailOk = data?.email?.ok;
  const recipients = data?.recipients || [];
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-lg w-full rounded-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="reset-password-modal"
      >
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-emerald-50">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 size={18} />
            <div>
              <div className="text-xs uppercase tracking-[0.22em]">
                Senha redefinida
              </div>
              <div className="text-sm font-semibold">
                Guarde ou copie a senha temporária abaixo
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-800"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
            Senha temporária
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code
              data-testid="reset-password-value"
              className="flex-1 font-mono text-lg tracking-wider bg-slate-100 border border-slate-200 px-3 py-2.5 rounded-sm select-all"
            >
              {data.temporary_password}
            </code>
            <button
              onClick={onCopy}
              className="inline-flex items-center gap-2 border border-slate-300 px-3 py-2 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
            >
              <Copy size={14} /> {copied ? "Copiado!" : "Copiar"}
            </button>
          </div>

          <div className="mt-5 pt-4 border-t border-slate-200">
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">
              E-mail
            </div>
            {recipients.length === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
                Sem destinatários cadastrados (e-mail principal e do representante
                legal vazios). Repasse manualmente ao associado.
              </div>
            ) : emailOk ? (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-sm">
                Enviado para: {recipients.join(", ")}
              </div>
            ) : (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">
                Falha ao enviar o e-mail para {recipients.join(", ")}. Copie a
                senha e repasse manualmente.
              </div>
            )}
          </div>

          <div className="mt-5 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
